/**
 * Session State Management for Mobile Deeplink Flow
 *
 * Manages session state across deeplink redirects using sessionStorage.
 * This is critical for the two-phase mobile authentication flow where
 * the page context is lost during the deeplink redirect.
 *
 * @module utils/session
 */

/**
 * Types of pending operations
 */
export type OperationType = 'enroll' | 'verify';

/**
 * Session state data structure
 */
export interface SessionState {
  /**
   * Type of operation (enrollment or verification)
   */
  operation: OperationType;

  /**
   * Username (for enrollment)
   */
  username?: string;

  /**
   * User ID (base64-encoded)
   */
  userId?: string;

  /**
   * Domain/rpId
   */
  domain: string;

  /**
   * Display name (for enrollment)
   */
  displayName?: string;

  /**
   * Challenge (base64-encoded)
   */
  challenge?: string;

  /**
   * Credential ID (base64-encoded, for verification)
   */
  credentialId?: string;

  /**
   * Custom authentication options
   */
  customOptions?: {
    checkLiveness?: boolean;
    checkAge?: boolean;
    checkAgeAboveThreshold?: boolean;
    checkGeoLocation?: boolean;
    ageThreshold?: number;
  };

  /**
   * Verification mode
   */
  verification?: 'none' | 'default' | { custom: { endpoint: string; apiKey: string } };

  /**
   * Allowed credentials for authentication
   */
  allowCredentials?: string[];

  /**
   * Timeout value
   */
  timeout?: number;

  /**
   * Timestamp when the state was saved
   */
  timestamp: number;
}

/**
 * Storage key for session state
 */
const STORAGE_KEY = 'fido2_pending_operation';

/**
 * Maximum age for session state (5 minutes)
 * After this time, the state is considered stale and will be ignored
 */
const MAX_STATE_AGE = 5 * 60 * 1000;

/**
 * Checks if sessionStorage is available
 *
 * @returns true if sessionStorage is available
 */
function isSessionStorageAvailable(): boolean {
  try {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    // Test if we can actually use it
    const testKey = '__fido2_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Saves pending operation state to sessionStorage
 *
 * This is called before redirecting to the mobile app via deeplink.
 * The state will be retrieved after the app redirects back.
 *
 * @param state - Session state to save
 *
 * @throws {Error} If sessionStorage is not available
 *
 * @example
 * ```typescript
 * savePendingOperation({
 *   operation: 'enroll',
 *   username: 'john.doe',
 *   domain: 'example.com',
 *   timestamp: Date.now()
 * });
 * ```
 */
export function savePendingOperation(state: SessionState): void {
  if (!isSessionStorageAvailable()) {
    throw new Error(
      'sessionStorage is not available. ' +
        'Mobile deeplink flow requires sessionStorage to preserve state across redirects.'
    );
  }

  try {
    const stateWithTimestamp: SessionState = {
      ...state,
      timestamp: Date.now()
    };

    const serialized = JSON.stringify(stateWithTimestamp);
    sessionStorage.setItem(STORAGE_KEY, serialized);

    // Mirror into localStorage as well. sessionStorage is scoped per browsing context, and iOS
    // routinely opens the deeplink callback in a NEW TAB — where sessionStorage is empty. The
    // callback then had to rebuild the operation from URL params, which carry only op/username/
    // apiKey/verification, silently dropping customOptions (so no PRF salt reached Phase 2),
    // along with the challenge and allowCredentials.
    //
    // localStorage is shared across tabs of the same origin, so the real state survives. It is
    // still age-limited by MAX_STATE_AGE and cleared alongside the sessionStorage copy.
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, serialized);
      }
    } catch {
      // Non-fatal: the sessionStorage copy is the primary, this is only the cross-tab fallback.
    }
  } catch (error) {
    throw new Error(
      `Failed to save pending operation to sessionStorage: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Retrieves pending operation state from sessionStorage
 *
 * This is called after the mobile app redirects back to the website.
 * Returns null if no state exists or if the state is stale.
 *
 * @returns The session state, or null if not found or stale
 *
 * @example
 * ```typescript
 * const state = getPendingOperation();
 * if (state) {
 *   console.log('Operation type:', state.operation);
 *   console.log('Domain:', state.domain);
 * }
 * ```
 */
export function getPendingOperation(): SessionState | null {
  try {
    // Prefer sessionStorage (tab-local, so unambiguous), then fall back to the localStorage
    // mirror — which is what survives when iOS opens the callback in a new tab.
    let stateJson: string | null = null;

    if (isSessionStorageAvailable()) {
      stateJson = sessionStorage.getItem(STORAGE_KEY);
    }

    if (!stateJson && typeof localStorage !== 'undefined') {
      stateJson = localStorage.getItem(STORAGE_KEY);
    }

    if (!stateJson) {
      return null;
    }

    const state: SessionState = JSON.parse(stateJson);

    // Check if state is stale
    const age = Date.now() - state.timestamp;
    if (age > MAX_STATE_AGE) {
      // State is too old, clear it
      clearPendingOperation();
      return null;
    }

    return state;
  } catch (error) {
    // If parsing fails, clear the corrupted state
    clearPendingOperation();
    return null;
  }
}

/**
 * Clears pending operation state from sessionStorage
 *
 * This should be called after successfully completing the operation
 * or when cancelling/aborting.
 *
 * @example
 * ```typescript
 * // After successfully completing WebAuthn ceremony
 * clearPendingOperation();
 * ```
 */
export function clearPendingOperation(): void {
  try {
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    // Clear the cross-tab mirror too, or a stale operation would outlive the tab that made it.
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    // Silently fail if removal fails
    console.warn('Failed to clear pending operation:', error);
  }
}

/**
 * Checks if there is a pending operation
 *
 * @returns true if there is a valid pending operation
 *
 * @example
 * ```typescript
 * if (hasPendingOperation()) {
 *   // Handle callback from mobile app
 *   await sdk.handleDeeplinkCallback();
 * }
 * ```
 */
export function hasPendingOperation(): boolean {
  return getPendingOperation() !== null;
}

/**
 * Gets the age of the current pending operation in milliseconds
 *
 * @returns Age in milliseconds, or null if no pending operation
 *
 * @example
 * ```typescript
 * const age = getPendingOperationAge();
 * if (age && age > 60000) {
 *   console.log('Operation is over 1 minute old');
 * }
 * ```
 */
export function getPendingOperationAge(): number | null {
  const state = getPendingOperation();
  if (!state) {
    return null;
  }
  return Date.now() - state.timestamp;
}
